import { Form, FormItem, useForm } from '@magicdoorinc/form';
import { LabeledSelect, LabeledTextArea, Modal, toast, useLocalization } from '@magicdoorinc/ui';
import { createEffect, createSignal, on } from 'solid-js';
import { useAuth, useCurrentCompany } from '~/contexts/global';
import { Dict } from '~/utils/dict';
import type { Component } from 'solid-js';

const feedbackCategoryDict = new Dict([
  { value: 'bug_report', label: 'Bug report' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'general_feedback', label: 'General feedback' },
]);

type FeedbackFormData = {
  category: string;
  message: string;
};

export const FeedbackModal: Component<{
  open: boolean;
  onClose: () => void;
}> = (props) => {
  const { t } = useLocalization();
  const form = useForm();
  const { me } = useAuth();
  const { currentCompany } = useCurrentCompany();
  const [submitting, setSubmitting] = createSignal(false);

  const categoryOptions = feedbackCategoryDict.getOptions();

  createEffect(
    on(
      () => props.open,
      (isOpen) => {
        if (isOpen) {
          form.setFormStore({ category: '', message: '' });
          setSubmitting(false);
        }
      }
    )
  );

  const handleSubmit = async () => {
    if (submitting()) return;
    const { validateStatus } = form.validateForm();
    if (!validateStatus) return;

    const formData = form.formStore as FeedbackFormData;
    const webhookUrl = import.meta.env.VITE_SLACK_FEEDBACK_WEBHOOK_URL as string;

    setSubmitting(true);
    try {
      const blocks: object[] = [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: `New Feedback: ${feedbackCategoryDict.getLabelByValue(formData.category, true)}`,
          },
        },
        {
          type: 'section',
          fields: [
            { type: 'mrkdwn', text: `*Submitted by:*\n${me()?.name ?? 'Unknown'} (${me()?.email ?? 'Unknown'})` },
            { type: 'mrkdwn', text: `*Company:*\n${currentCompany()?.name ?? 'Unknown'} (ID: ${currentCompany()?.id ?? 'Unknown'})` },
            { type: 'mrkdwn', text: `*Category:*\n${feedbackCategoryDict.getLabelByValue(formData.category, true)}` },
          ],
        },
        {
          type: 'section',
          text: { type: 'mrkdwn', text: '*Message:*' },
        },
        {
          type: 'section',
          text: { type: 'plain_text', text: formData.message },
        },
      ];

      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `payload=${encodeURIComponent(JSON.stringify({ blocks }))}`,
      });

      toast.success(t('Feedback submitted successfully'));
      props.onClose();
    } catch {
      toast.error(t('Failed to submit feedback'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={props.open}
      title={t('Submit feedback')}
      onDone={handleSubmit}
      doneText={t('Submit')}
      loading={submitting()}
      disabled={submitting()}
      class="lg:w-modalSm"
      onCancel={props.onClose}>
      <div class="p-6">
        <Form class="space-y-4" defaultForm={form}>
          <FormItem
            label={t('Category')}
            formFieldName="category"
            rules={[{ required: true, message: t('Please select a category') }]}
            component={LabeledSelect}
            options={categoryOptions}
          />
          <FormItem
            label={t('Message')}
            formFieldName="message"
            rules={[{ required: true, message: t('Please enter your feedback') }]}
            placeholder={t('Please enter your feedback')}
            component={LabeledTextArea}
            rows={5}
          />
        </Form>
      </div>
    </Modal>
  );
};
